import { Fragment, type ReactElement, useMemo, type ReactNode } from 'react'
import { type InfoFetcherProps } from 'app/components/entityInfo/types'
import { isDefined, isEmpty, isNotEmpty, isUserId, type TinyUser, type UserId } from '@terros/common'
import { Combobox, Divider } from '@mantine/core'
import { Size, useProfile, useTranslation } from '@front/model'
import { PrefetchedUserInfo, SelectableOption, UserInfo } from '@components'

export type UserInlineSelectExtraOption = {
  value: UserId
  label: string
}

type UseDropdownContentProps = {
  enabled?: boolean
  users: TinyUser[] | undefined
  selected: UserId[]
  query: string
  allowEmpty?: boolean
  emptyOptionLabel?: string
  isLoading: boolean
  hideSelfOption?: boolean
  renderOptionDetails?: (userId: UserId) => ReactNode
  extraOptions?: UserInlineSelectExtraOption[]
}
export const useDropdownContent = ({
  enabled = true,
  users,
  selected,
  query,
  allowEmpty,
  emptyOptionLabel = 'All users',
  isLoading,
  hideSelfOption = false,
  renderOptionDetails,
  extraOptions,
}: UseDropdownContentProps): ReactNode => {
  const { user: self } = useProfile()
  const { t } = useTranslation()
  const options = useMemo(() => {
    if (!enabled) return []
    const options = []
    const extraValues = new Set(extraOptions?.map((option) => option.value))

    if (isEmpty(query)) {
      if (allowEmpty) {
        options.push(
          <SelectableOption key='all-users' selected={isEmpty(selected)} value='all'>
            {t(emptyOptionLabel)}
          </SelectableOption>
        )
      }
      if (isNotEmpty(extraOptions)) {
        for (const extra of extraOptions) {
          options.push(
            <SelectableOption key={extra.value} selected={selected.includes(extra.value)} value={extra.value}>
              {extra.label}
            </SelectableOption>
          )
        }
      }
      if (allowEmpty || isNotEmpty(extraOptions)) {
        options.push(<Divider key='extras-divider' my={Size.space.lg} />)
      }
      const shouldShowSelf = isDefined(self) && !hideSelfOption
      if (shouldShowSelf) {
        options.push(
          <Fragment key={self.userId}>
            <UserOption user={self} selected={selected.includes(self.userId)} renderExtra={renderOptionDetails} />
            <Divider my={Size.space.lg} />
          </Fragment>
        )
      }
      for (const userId of selected) {
        if (isNotEmpty(users) && users.some((user) => user.userId === userId)) continue
        if (shouldShowSelf && userId === self.userId) continue
        if (extraValues.has(userId)) continue
        options.push(<UserOption user={userId} selected key={userId} renderExtra={renderOptionDetails} />)
      }
    }

    if (isNotEmpty(users)) {
      options.push(
        users.map((user) => {
          if (user.userId === self?.userId && isEmpty(query)) return
          const isSelected = selected.includes(user.userId)
          return <UserOption user={user} selected={isSelected} key={user.userId} renderExtra={renderOptionDetails} />
        })
      )
    }

    return options
  }, [
    enabled,
    users,
    selected,
    query,
    self,
    t,
    allowEmpty,
    emptyOptionLabel,
    hideSelfOption,
    renderOptionDetails,
    extraOptions,
  ])

  const emptyMessage = useMemo(() => {
    if (!enabled) return null
    if (isLoading) return <Combobox.Empty>Loading...</Combobox.Empty>
    if (isNotEmpty(users)) return <Combobox.Empty>{t('Type to search for more users')}</Combobox.Empty>
    if (isEmpty(query)) return <Combobox.Empty>{t('No users found')}</Combobox.Empty>
    return <Combobox.Empty>{t('No users found matching your search')}</Combobox.Empty>
  }, [enabled, users, isLoading, t, query])

  return (
    <>
      {options}
      {emptyMessage}
    </>
  )
}

const UserOption = ({
  user,
  selected,
  renderExtra,
}: {
  user: UserId | TinyUser
  selected: boolean
  renderExtra?: (userId: UserId) => ReactNode
}): ReactElement => {
  const commonProps: InfoFetcherProps = {
    size: 'sm',
    linked: false,
    textProps: {
      size: 'sm',
      lineClamp: undefined,
      className: 'flex-1',
    },
    details: renderExtra?.(isUserId(user) ? user : user.userId),
  }

  const value = isUserId(user) ? user : user.userId

  return (
    <SelectableOption selected={selected} value={value}>
      {isUserId(user) ? (
        <UserInfo userId={user} {...commonProps} />
      ) : (
        <PrefetchedUserInfo user={user} {...commonProps} />
      )}
    </SelectableOption>
  )
}
